import { initializeCircles } from '../../utils';
import {
	DRIVER,
	DRIVER_ICON_URL,
	SHOP_ICON_URL,
	CUSTOMER_ICON_URL,
} from '../../constants';
import store from '../../redux/store';
import { logMediumError } from '../../utils/logger';

type MapClientRefs = {
	map: any;
	mapCanvas: HTMLDivElement | null;
	directionsDisplay: any;
	routePolyline: any; // AJOUT
	orderMarker: any;
	customerMarker: any;
	circle1: any;
	circle2: any;
	orderLocation: any;
};

const getGoogle = () => (window as unknown as { google: any }).google;

const createMarker = (position: any, icon: any, map: any) => {
	const google = getGoogle();
	return new google.maps.Marker({
		position,
		map,
		icon,
	});
};

export const initializeMap = (
	refs: MapClientRefs,
	options: any = {}
) => {
	if (refs.map) return;
	const google = getGoogle();
	if (!refs.mapCanvas) return;
	const defaultOptions = {
		mapTypeId: google.maps.MapTypeId.ROADMAP,
		disableDefaultUI: true,
		zoom: 12,
	};
	const myOptions = {
		...defaultOptions,
		...options,
	};
	refs.map = new google.maps.Map(
		refs.mapCanvas,
		myOptions
	);
};

export const initializeMapWithAddress = (
	address: any,
	refs: MapClientRefs
) => {
	try {
		const { latitude, longitude } = address;
		const { secondaryColor } =
			store.getState().configurationReducer;
		const center = {
			lat: latitude,
			lng: longitude,
		};
		initializeMap(refs, {
			center,
			zoom: 12,
		});
		initializeCircles(
			refs as any,
			center,
			secondaryColor
		);
	} catch (error) {
		logMediumError(
			`Error initializing map with address: ${error}`,
			{ address }
		);
	}
};

export const updateMap = (
	customerLocation: any,
	refs: MapClientRefs
) => {
	try {
		const google = getGoogle();
		const { map } = refs;
		if (!map) return;

		const point = {
			lat: Number(customerLocation.latitude),
			lng: Number(customerLocation.longitude),
		};
		const bounds = new google.maps.LatLngBounds();
		new google.maps.Marker({
			position: point,
			map,
		});
		bounds.extend(point);
		map.setCenter(point);
		map.setZoom(12);

		if (refs.circle1) refs.circle1.setCenter(point);
		if (refs.circle2) refs.circle2.setCenter(point);
	} catch (error) {
		logMediumError(`Error updating map: ${error}`);
	}
};

export const updateMapWithDriver = (
	driverLocation: any,
	customerLocation: any,
	mode: string,
	refs: MapClientRefs
) => {
	try {
		const google = getGoogle();

		const { map } = refs;

		if (!map) return;

		const driverIcon = {
			url: DRIVER_ICON_URL,
			scaledSize: new google.maps.Size(70, 70),
			anchor: new google.maps.Point(35, 35),
		};

		const customerIcon = {
			url: CUSTOMER_ICON_URL,
			scaledSize: new google.maps.Size(40, 40),
		};

		const shopIcon = {
			url: SHOP_ICON_URL,
			scaledSize: new google.maps.Size(40, 40),
		};

		if (refs.orderMarker) {
			refs.orderMarker.setMap(null);
		}

		if (refs.customerMarker) {
			refs.customerMarker.setMap(null);
		}

		const start = {
			lat: Number(driverLocation.latitude),
			lng: Number(driverLocation.longitude),
		};

		const end = {
			lat: Number(customerLocation.latitude),
			lng: Number(customerLocation.longitude),
		};

		refs.orderMarker = createMarker(
			start,
			mode === DRIVER ? driverIcon : shopIcon,
			map
		);

		refs.customerMarker = createMarker(
			end,
			customerIcon,
			map
		);

		const bounds = new google.maps.LatLngBounds();

		bounds.extend(start);
		bounds.extend(end);

		map.fitBounds(bounds);

		if (refs.directionsDisplay) {
			refs.directionsDisplay.setMap(null);
			refs.directionsDisplay = null;
		}
	} catch (error) {
		logMediumError(
			`Error updating map with driver: ${error}`
		);
	}
};

export const updateMapDirectionFromBackend = (
	response: any,
	orderLocation: any,
	customerLocation: any,
	mode: string,
	refs: MapClientRefs
) => {
	try {
		const google = getGoogle();
		const { map } = refs;
		if (!map) return;
		const route = response?.routes?.[0];
		if (!route) return;

		const driverIcon = {
			url: DRIVER_ICON_URL,
			scaledSize: new google.maps.Size(70, 70),
			anchor: new google.maps.Point(35, 35),
		};
		const customerIcon = {
			url: CUSTOMER_ICON_URL,
			scaledSize: new google.maps.Size(40, 40),
		};
		const shopIcon = {
			url: SHOP_ICON_URL,
			scaledSize: new google.maps.Size(40, 40),
		};

		if (refs.orderMarker) {
			refs.orderMarker.setMap(null);
		}
		if (refs.customerMarker) {
			refs.customerMarker.setMap(null);
		}

		const start = {
			lat: Number(orderLocation.latitude),
			lng: Number(orderLocation.longitude),
		};

		const end = {
			lat: Number(customerLocation.latitude),
			lng: Number(customerLocation.longitude),
		};

		refs.orderMarker = createMarker(
			start,
			mode === DRIVER ? driverIcon : shopIcon,
			map
		);
		refs.customerMarker = createMarker(
			end,
			customerIcon,
			map
		);

		if (refs.routePolyline) {
			refs.routePolyline.setMap(null);
		}

		const encodedPolyline =
			route.overview_polyline?.points;

		if (!encodedPolyline) return;

		const decodedPath =
			google.maps.geometry.encoding.decodePath(
				encodedPolyline
			);

		refs.routePolyline =
			new google.maps.Polyline({
				path: decodedPath,
				strokeColor: '#6E747C',
				strokeWeight: 3,
				map,
			});

		if (!refs.routePolyline) {
			const bounds = new google.maps.LatLngBounds();

			decodedPath.forEach((point: any) => {
				bounds.extend(point);
			});

			map.fitBounds(bounds);
		}
	} catch (error) {
		logMediumError(
			`Error displaying backend directions: ${error}`
		);
	}
};